/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Component which shows all review requests in the page with all 
 */

import {
  ReactiveController,
  ReactiveControllerHost,
  css,
  html,
  TemplateResult
} from "lit";
import { customElement, property } from "lit/decorators.js";

import "@maicol07/material-web-additions/data-table/data-table";
import "@maicol07/material-web-additions/data-table/data-table-row";
import "@maicol07/material-web-additions/data-table/data-table-column";
import "@maicol07/material-web-additions/data-table/data-table-cell";

import "@material/mwc-list/mwc-list";
import "@material/mwc-list/mwc-check-list-item";
import "@material/mwc-button";
import "@material/mwc-textfield";
import "@material/mwc-linear-progress";
import "@material/mwc-circular-progress";

import {ReviewRequest} from "../data-classes/review-request";
import { fetchRetry } from "../fetch-retry";
import { HitlElement } from "../hitl-element";

/**
 * @type {QueueType} type - The queue type
 */
export type QueueType = "File" | "PubSub" | "Database";

/**
 * Component to manage review-requests
 */
@customElement("hitl-review-requests")
export class HitlReviewRequests extends HitlElement {
  /**
   * Selected review-requests
   */
  selectedReviewRequests: ReviewRequest[] = [];

  /**
   * Queue selection
   */
  queueSelection: number[] = [];

  /**
   * Boolean to proof loading
   */
  @property({ type: Boolean })
  loading: boolean = false;

  /**
   * Project id
   */
  projectId?: string = process.env.PROJECT_ID;
  /**
   * Dialog text
   */
  dialogText = document.createElement('p');

  /**
   * Ok Button for dialog
   */
  footerButtonOk = document.createElement('mwc-button');

  /**
   * Cancel button for dialog
   */
  footerButtonCancel = document.createElement('mwc-button');

  /**
   * Temp selected rows
   */
  @property({type: Array})
  tempSelectedRows =[];

  /**
   * Timer id for connected- and callback
   */
  private timerId: any;
  
  static override styles = css`
  
    .mdc-card > div {
      margin: 0px 12px 0 12px;
    }

    .mdc-card__actions {
      gap: 12px;
    }
    html, body {
      --mdc-data-table-width: 70.0vmin;
    }

    md-data-table {
      width: 95.0vw;
    }

    md-data-table-footer {
      gap: 10px;
      align-items: center;
    }

    mwc-button {
      margin-top: 8px;
      font-size: 2.4vmin;
    }

    md-data-table-cell {
      
      background-color: #ffffff;
      
    }

    md-data-table-column {
      background-color: #ffffff;
      
      overflow: hidden;
    }

    mdc-data-table-footer {
      background-color: #ffffff;
      font-size: 2.6vmin;
    }

    mwc-circular-progress{
        justify-content: center;
    }

  `;

  constructor() {
    super();
  }

  protected override async firstUpdated() {
    //Initialization review-requests
    if (this.database) {
      const accessToken = await this.getAccessToken();
      this.reviewRequests = await ReviewRequest.list(
        this.database,
        accessToken
      );
      this.tempReviewRequests = this.reviewRequests;
    }
  }

  override render() {
    const template: TemplateResult[] = [];
    template.push(html`
      <div class="mdc-card">
        <div class="card-header">
          <div class="card-title">
            <h2 class="mdc-typography--headline6">Aufgaben</h2>
          </div>
        </div>
        <div class="card-body mdc-typography--body2">
        <md-data-table
          id="dataTable"
          @selected-all=${this.rowSelectionChanged}
          @row-selection-changed=${this.rowSelectionChanged}
          @unselected-all=${this.rowSelectionChanged}
          aria-label="Review Request"
          density=""
          inProgress= true
          page-sizes="[10, 25, 100]"
          page-sizes-label="Zeilen pro Seite:"
          first-row-of-page=""
          current-page-size=""
          last-row-of-page=""
          paginated="true"
          pagination-total-label=":firstRow-:lastRow of :totalRows">
          <md-data-table-column type="checkbox" style="color: green;"></md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Mandantenname
          </md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Mandantennummer
          </md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Beratername
          </md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Dokumentenname
          </md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Status
          </md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Letzte Änderung am
          </md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Geprüft von
          </md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Hochgeladen von
          </md-data-table-column>
          <md-data-table-column sortable="" filterable="" filter-text-field-label="Suchen">
            Erstellungsdatum
          </md-data-table-column>
          ${this.tempReviewRequests.map((reviewRequest: ReviewRequest) => {
            const date = new Date(reviewRequest.createTime)
            const createTime = date.toLocaleDateString('de', {hour:'numeric', minute:'numeric', day: '2-digit', month: '2-digit', year: 'numeric'});
            const dateUpdate = new Date(reviewRequest.lastStatusUpdateTime);
            /**
             * Last time the reviewRequest has be changed
             */
            const lastStatusUpdateTime = dateUpdate.toLocaleDateString('de', {hour:'numeric', minute:'numeric', day: '2-digit', month: '2-digit', year: 'numeric'});    
            return html`
              <md-data-table-row data-id=${reviewRequest.reviewRequestID}>
                <md-data-table-cell type="checkbox"></md-data-table-cell>
                <md-data-table-cell>
                  ${reviewRequest.clientName}
                </md-data-table-cell>
                <md-data-table-cell type="numeric">
                  ${reviewRequest.clientId}
                </md-data-table-cell>
                <md-data-table-cell>
                  ${reviewRequest.consultantName}
                </md-data-table-cell>
                <md-data-table-cell>${reviewRequest.originalFilename}</md-data-table-cell>
                <md-data-table-cell>
                    <div id="currentStatus" style="display: inline; vertical-align: middle;">${reviewRequest.status}</div>
                    <div id="statusLoad"style="display: inline;">
                        ${reviewRequest.status == "wird verarbeitet" || reviewRequest.status == "in Bearbeitung"
                            ?
                            html`<mwc-circular-progress indeterminate density=-7></mwc-circular-progress>`
                            :null}
                    </div>
                </md-data-table-cell>
                <md-data-table-cell>
                  ${lastStatusUpdateTime}
                </md-data-table-cell>
                <md-data-table-cell>
                  ${reviewRequest.reviewedByUserId}
                </md-data-table-cell>
                <md-data-table-cell>
                  ${reviewRequest.uploadedByUserId}
                </md-data-table-cell>
                <md-data-table-cell>
                  ${createTime}
                </md-data-table-cell>
              </md-data-table-row>
            `;
          })}
          <md-data-table-footer
            slot="footer"
            style="display: flex; align-items: left; justify-content: left;">
            <mwc-button
              raised
              @click=${this.reviewBatch}
              label="Dokument(e) prüfen"
              ?disabled=${this.selectedReviewRequests.length === 0 || this.selectedReviewRequests.filter((reviewRequest: ReviewRequest) => reviewRequest.originalStatus == "hitl").length < this.selectedReviewRequests.length}></mwc-button>
            <mwc-button
              raised              
              label="Dokument(e) herunterladen"
              @click=${this.downloadBatch}
              ?disabled=${this.selectedReviewRequests.length === 0 || this.selectedReviewRequests.filter((reviewRequest: ReviewRequest) => reviewRequest.originalStatus == "completed").length < this.selectedReviewRequests.length}></mwc-button>
            <mwc-button
              raised              
              label="Dokument(e) löschen"
              @click=${this.deleteBatch}
              ?disabled=${this.selectedReviewRequests.length === 0}></mwc-button>
          </md-data-table-footer>
        </md-data-table>
      </div>
    `);
    return template;
  }
  
  /**
   * Review batch
   */
  async reviewBatch() {
    // ensure that the access token gets refreshed if necessary
    await this.getAccessToken(true);
    const reviewRequestUpdatedEvent = new CustomEvent(
      "batch-review-triggered",
      {
        bubbles: true,
        detail: this.selectedReviewRequests,
      }
    );
    this.dispatchEvent(reviewRequestUpdatedEvent);
  }

  /**
   * Initializes the dialog
   */
  initDialog() {
    this.dialog!.innerHTML ="";
    this.footerButtonOk.innerHTML ="";
    this.footerButtonCancel.innerHTML ="";
    this.footerButtonOk.setAttribute("slot", "primaryAction");
    this.footerButtonOk.setAttribute("dialogAction", "ok");
    this.footerButtonOk.innerText = "Ok";
    this.footerButtonOk.classList.add("raised");
    this.footerButtonCancel.setAttribute("slot", "secondaryAction");
    this.footerButtonCancel.setAttribute("dialogAction", "cancel");
    this.footerButtonCancel.innerText = "Abbrechen";
    this.footerButtonCancel.classList.add("raised");
    this.dialogText.style.padding = "0px 20%";
    this.dialog!.setAttribute('style', 'border-color: #000000; border-width: 1px; color: #000000; font-size: 1.1em;');
  }

  /**
   * Deletes the batch of one or more review-requests
   * @param e 
   */
  async deleteBatch(e: CustomEvent) {
    e.preventDefault();
    const accessToken = await this.getAccessToken(true);
    this.initDialog();
    //Generates text for the user, if a single document or multiple documents are selected
    if(this.selectedReviewRequests.length > 1){
        this.dialogText.innerHTML="Sind Sie sicher, das Sie die ausgewählten Dokumente löschen möchten?";
    } else this.dialogText.innerHTML="Sind Sie sicher, das Sie das ausgewählte Dokument löschen möchten?";


    this.footerButtonOk.addEventListener('click', ()=>{
      const nextDialog = this.dialog!;
      const okButton = document.createElement('mwc-button');
        okButton.setAttribute("dialogAction", "ok");
        okButton.innerText = "Ok";
        okButton.classList.add("raised");
        this.dialogText.innerHTML="";
        this.dialogText.innerHTML = "Dokumente werden gelöscht.."
        nextDialog.innerHTML ="";
        //Circular progress bar
        const circularProgress = document.createElement('mwc-circular-progress');
        circularProgress.setAttribute('density', '-1');
        circularProgress.setAttribute('indeterminate', 'true');
            nextDialog.append(circularProgress);
            nextDialog.appendChild(this.dialogText);
            nextDialog.style.textAlign="-webkit-center";
            nextDialog.appendChild(okButton);
            nextDialog.open=true;
        //Check if there is review-request selected    
        if(this.selectedReviewRequests.length > 0){
            const reviewRequestIds = this.selectedReviewRequests.map(
            (reviewRequest: ReviewRequest) => reviewRequest.reviewRequestID); 
          ReviewRequest.deleteReviewRequests(this.database!, accessToken, reviewRequestIds).then(()=>{
              console.log(nextDialog);
              this.tempSelectedRows.forEach((row:any) =>{
                  row.selected = false;
              });
              nextDialog.open = false;
              nextDialog.innerHTML = "";
              this.tempReviewRequests = this.reviewRequests.filter((reviewRequest: ReviewRequest) => 
                    !(this.selectedReviewRequests.includes(reviewRequest)));
              this.selectedReviewRequests=[];
          });          
          this.requestUpdate();
        }
    });
    this.dialog!.appendChild(this.dialogText);
    this.dialog!.appendChild(this.footerButtonCancel);
    this.dialog!.appendChild(this.footerButtonOk);
    this.dialog!.open = true;
    this.dialog!.addEventListener('closed', ()=> {
        this.footerButtonOk.innerHTML ="";
        this.dialog!.innerHTML="";
    });
  }

  /**
   * Download batch. Download the splitted PDFs
   */
  async downloadBatch() {
    const dirHandle = await window.showDirectoryPicker({mode: "readwrite"});
    const accessToken = await this.getAccessToken(true);
    this.initDialog();
    this.dialogText.innerHTML = "Ihre Dokumente werden heruntergeladen.."
    this.dialog!.innerHTML="";

    /**
     * Circular progress https://www.npmjs.com/package/@material/mwc-circular-progress
     */
    const circularProgress = document.createElement('mwc-circular-progress');
    circularProgress.setAttribute('density', '-1');
    circularProgress.setAttribute('indeterminate', 'true');
    
    this.dialog!.append(circularProgress);
    this.dialog!.style.textAlign="-webkit-center";
    this.dialog!.appendChild(this.dialogText);
    this.dialog!.appendChild(this.footerButtonOk);
    this.dialog!.open = true;
    
    //The filename of each splitted document will be set, based on their type and page number
    this.selectedReviewRequests.forEach(async reviewRequest => {
      reviewRequest.splittedPdfObjects.forEach(async splittedPdfObject => {
        console.log(splittedPdfObject);
        let indexDocType: number = 0;
        let leadingZeroSet: string ="";
        const indexSecondUnderscore = reviewRequest.originalFilename.indexOf('_', reviewRequest.originalFilename.indexOf('_') + 1);
        const clientConsultantValues = reviewRequest.originalFilename.substr(0, indexSecondUnderscore + 1);
        const subStringFilenamePDF = reviewRequest.originalFilename.substr(indexSecondUnderscore + 1, reviewRequest.originalFilename.length);
        const subStringFilename = subStringFilenamePDF.substr(0, subStringFilenamePDF.length - 4)
        let label;
        switch(true){
            case splittedPdfObject.toLowerCase().includes("rechnung"):
                label = "RE";
                indexDocType = splittedPdfObject.toLowerCase().indexOf("rechnung");
                break;
            case splittedPdfObject.toLowerCase().includes("anhang"):
                label = "AN";
                indexDocType = splittedPdfObject.toLowerCase().indexOf("anhang");
                break;
            case splittedPdfObject.toLowerCase().includes("sonstiges"):
                label = "SO";
                indexDocType = splittedPdfObject.toLowerCase().indexOf("sonstiges");
                break;
        }
        leadingZeroSet = splittedPdfObject.toLowerCase().substring(indexDocType-8, indexDocType-1);

        const fileName = clientConsultantValues+label+"_"+subStringFilename+"-"+leadingZeroSet+".pdf";
        const fileHandle = await dirHandle.getFileHandle(fileName, {create: true});
        const fileStream = await fileHandle.createWritable();
        const splittedPdfUrl = "https://" + reviewRequest.resultBucket + ".storage.googleapis.com/" + splittedPdfObject
        const response = await fetchRetry(splittedPdfUrl,{
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        });        
        const blob = await response.blob();
        await fileStream.write(blob);
        await fileStream.close().then(() => {
            this.dialog!.open = false
            
        });
      });
      // set status to downloaded
      if (this.database) {
        reviewRequest.updateStatus(this.database, accessToken, "downloaded");
        reviewRequest.status = "heruntergeladen";
        this.tempSelectedRows.forEach((row:any) =>{
          row.selected = false;
      });
      }
    });
  }
  
  override connectedCallback() {
    super.connectedCallback();
    //Interval in which review-requests are updated each 10 sec. = 10000 msec.
    this.timerId = setInterval(async () => {
      if (this.database) {
        const accessToken = await this.getAccessToken();
        this.reviewRequests = await ReviewRequest.list(
          this.database,
          accessToken
        );
        this.tempReviewRequests = this.reviewRequests;
      }
      this.requestUpdate();
      console.log("requestUpdate called");
    }, 10000);
      }
  
  override disconnectedCallback() {
    super.disconnectedCallback();
    //Interval for review-requests get disconnected after leaving the page with this component
    if (this.timerId) clearInterval(this.timerId);
    console.log("Clear Interval from disconnectedCallback");
      }

  /**
   * Special event from the package maicol07/material-web-additions/data-table/data-table.
   * It triggers if the row selection in the data-table changes
   * @param e 
   */
  rowSelectionChanged = async (e: CustomEvent) => {
    this.tempSelectedRows = e.target!.rows!.filter((row: any) => row.selected);
    this.selectedReviewRequests = e
      .target!.rows!.filter((row) => row.selected)
      .map((row) =>
        this.tempReviewRequests.find(
          (reviewRequest) => reviewRequest.reviewRequestID === row.dataset.id
        )
      );
    console.log("rowSelectionChanged", e);
    console.log("selected ReviewRequests", this.selectedReviewRequests);
  };
  }

declare global {
  interface HTMLElementTagNameMap {
    "hitl-review-requests": HitlReviewRequests;
  }
}